import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FolderOpen, Plus, Check, MoreVertical, Type, Image as ImageIcon, Sparkles,  Clock, ShieldAlert, XCircle, MapPin, CheckCircle2, Volume2, AlertTriangle, AlertCircle, Activity, User, Coffee, Smartphone, MessageCircle, Copy, Scissors, Download, Settings, Play, Pause, Edit3, Save, ArrowRight, ArrowLeft, ChevronsLeft, ChevronsRight, X } from 'lucide-react';
import { PremiumBackground } from '../../../components/ui/PremiumBackground';

type Phase = 'START_BUTTON' | 'SCRIPT' | 'END';

export type TrackType = 'textual' | 'visual' | 'auditivo' | 'interativo';
type ClipType = TrackType | 'GROUP';
export type Clip = {
  id: string;
  type: ClipType;
  trackId: number; 
  startTime: number;
  duration: number;
  parentId?: string; // Hierarquia Unity3D (Pai)
  parameters?: Record<string, string | number | boolean>; // Contratos de Grupo
  view?: string;
  text?: string;
  audio?: string;
  step?: number;
};


export type AITask = {
  id: string;
  name: string;
  progress: number;
  efficiency: number; // 1 to 100
  status: 'running' | 'completed' | 'error';
};

export type Branch = {
  id: string;
  name: string;
  clips: Clip[];
  createdAt: number;
};

export type Track = {
  id: number;
  name: string;
  type: TrackType;
  color: string;
  order: number;
  hidden?: boolean;
};

export type Snapshot = {
  id: string;
  timestamp: number;
  tracks: Track[];
  clips: Clip[];
};

export type Project = {
  id: string;
  name: string;
  publishedSnapshotId?: string;
  snapshots: Snapshot[];
};

const INITIAL_TRACKS: Track[] = [
  { id: 0, name: 'Grupos', type: 'interativo', color: 'transparent', order: 0 },
  { id: 1, name: 'Textos', type: 'textual', color: 'transparent', order: 1 },
  { id: 2, name: 'Cenas', type: 'visual', color: 'transparent', order: 2 },
  { id: 3, name: 'Áudio', type: 'auditivo', color: 'transparent', order: 3 }
];

const INITIAL_CLIPS: Clip[] = [
  // INTRO LOGO (0s - 6s)
  { id: 'logo_anim_full', type: 'visual', trackId: 2, startTime: 0, duration: 2500, view: 'SCENE_LOGO_FULL' },
  { id: 'logo_anim_shield', type: 'visual', trackId: 2, startTime: 2500, duration: 3500, view: 'SCENE_LOGO_SHIELD' },

  // TRACK 0: VISUAL
  // Pacing ajustado: Espaçamento para o Áudio da Eliete
  { id: 'intro_eliete_text', type: 'textual', trackId: 1, startTime: 6000, duration: 3000, view: 'IMPACT_TEXT', text: 'CONHEÇA ELIETE' },
  { id: 'intro_eliete', type: 'visual', trackId: 2, startTime: 9000, duration: 4000, view: 'SCENE_INTRO_ELIETE' },
  
  // Pacing para Gabriel:
  { id: 'intro_gabriel_text', type: 'textual', trackId: 1, startTime: 13000, duration: 3000, view: 'IMPACT_TEXT', text: 'E SEU FILHO, GABRIEL' },
  { id: 'v2_0_alice_scene', type: 'visual', trackId: 2, startTime: 16000, duration: 9500, view: 'SCENE_INTRO_GABRIEL' },
  
  // O resto empurrado
  { id: 'time_640', type: 'textual', trackId: 1, startTime: 25500, duration: 2000, view: 'IMPACT_TEXT', text: '06:40' },
  { id: 'v2_1_alice_scene', type: 'visual', trackId: 2, startTime: 27500, duration: 4500, view: 'SCENE_VAN' },
  { id: 'time_650', type: 'textual', trackId: 1, startTime: 32000, duration: 2000, view: 'IMPACT_TEXT', text: '06:50' },
  { id: 'v2_2_alice_scene', type: 'visual', trackId: 2, startTime: 34000, duration: 5500, view: 'SCENE_PORTARIA' },
  { id: 'time_710', type: 'textual', trackId: 1, startTime: 39500, duration: 2000, view: 'IMPACT_TEXT', text: '07:10' },
  { id: 'v2_3_alice_scene', type: 'visual', trackId: 2, startTime: 41500, duration: 4000, view: 'SCENE_SALA_AULA', step: 0 },
  { id: 'v2_4_alice_scene', type: 'visual', trackId: 2, startTime: 45500, duration: 5000, view: 'SCENE_SALA_AULA', step: 1 },
  { id: 'v2_5_alice_scene', type: 'visual', trackId: 2, startTime: 50500, duration: 10500, view: 'SCENE_SISTEMA_AGE' },
  { id: 'time_7m', type: 'textual', trackId: 1, startTime: 61000, duration: 2500, view: 'IMPACT_TEXT', text: '7 MINUTOS DEPOIS...' },
  { id: 'v2_6_giuliun_scene', type: 'visual', trackId: 2, startTime: 63500, duration: 10000, view: 'SCENE_DESFECHO' },

  // TRACK 1: AUDITIVO
  { id: 'logo_audio', type: 'auditivo', trackId: 3, startTime: 2500, duration: 3500, audio: '/audio/story/slide_intro.wav' },
  { id: 'v2_intro_eliete_audio', type: 'auditivo', trackId: 3, startTime: 9000, duration: 4000, audio: '/audio/story/v2_0_eliete_intro.wav' },
  { id: 'v2_0_alice_audio', type: 'auditivo', trackId: 3, startTime: 16000, duration: 9500, audio: '/audio/story/v2_0_alice.wav' },
  { id: 'v2_1_alice_audio', type: 'auditivo', trackId: 3, startTime: 27500, duration: 4500, audio: '/audio/story/v2_1_alice.wav' },
  { id: 'v2_2_alice_audio', type: 'auditivo', trackId: 3, startTime: 34000, duration: 5500, audio: '/audio/story/v2_2_alice.wav' },
  { id: 'v2_3_alice_audio', type: 'auditivo', trackId: 3, startTime: 41500, duration: 4000, audio: '/audio/story/v2_3_alice.wav' },
  { id: 'v2_4_alice_audio', type: 'auditivo', trackId: 3, startTime: 45500, duration: 5000, audio: '/audio/story/v2_4_alice.wav' },
  { id: 'v2_5_alice_audio', type: 'auditivo', trackId: 3, startTime: 50500, duration: 10500, audio: '/audio/story/v2_5_alice.wav' },
  { id: 'v2_6_giuliun_audio', type: 'auditivo', trackId: 3, startTime: 63500, duration: 10000, audio: '/audio/story/v2_6_giuliun.wav' },

  { id: 'alice_voice', trackId: 3, type: 'auditivo', startTime: 0, duration: 4000, view: 'Olá, eu sou a Alice...', text: 'Voz Alice' }
];

export const EscolaPublicaLandingPage: React.FC = () => {
  const getAbsoluteTime = (clipId: string, currentClips: Clip[] = clips): number => {
    const clip = currentClips.find(c => c.id === clipId);
    if (!clip) return 0;
    if (clip.parentId) {
        return clip.startTime + getAbsoluteTime(clip.parentId, currentClips);
    }
    return clip.startTime;
  };

  const [aiTasks, setAiTasks] = useState<AITask[]>([]);
  const [branches, setBranches] = useState<Branch[]>([{id: 'main', name: 'Main', clips: INITIAL_CLIPS, createdAt: Date.now()}]);
  const [activeBranchId, setActiveBranchId] = useState('main');
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);
  const [isListening, setIsListening] = useState<{clipId: string} | null>(null);

  // Gemini Mock Function
  const handleGeminiVoiceTrigger = (clipId: string) => {
     addTelemetry(`[GEMINI_VOICE_TRIGGER=${clipId}]`);
     setIsListening({ clipId });
     
     // Simulando a fala e processamento
     setTimeout(() => {
        setIsListening(null);
        const taskId = 'task_' + Date.now();
        const efficiency = Math.floor(Math.random() * 100) + 1; // 1 a 100
        
        setAiTasks(prev => [...prev, {
            id: taskId,
            name: `Ajuste de Tempo e Áudio (${clipId})`,
            progress: 0,
            efficiency,
            status: 'running'
        }]);

        // Simulando o progresso da task
        let p = 0;
        const interval = setInterval(() => {
            p += Math.floor(Math.random() * 20) + 5;
            if (p >= 100) {
               p = 100;
               clearInterval(interval);
               
               // Quando conclui, cria um novo branch com a edição feita
               setAiTasks(curr => curr.map(t => t.id === taskId ? {...t, progress: 100, status: 'completed'} : t));
               
               setTimeout(() => {
                  setAiTasks(curr => curr.filter(t => t.id !== taskId));
                  
                  // Aplicar a modificação (Branching)
                  setBranches(prevBranches => {
                      const baseClips = prevBranches.find(b => b.id === activeBranchId)?.clips || clips;
                      // Simular edição
                      const newClips = baseClips.map(c => {
                          if (c.id === clipId) {
                             return { ...c, duration: c.duration + 500 }; // A IA "aumentou" a duração em 500ms
                          }
                          return c;
                      });
                      
                      const newBranch: Branch = {
                          id: 'branch_' + Date.now(),
                          name: `IA: Ajuste ${clipId}`,
                          clips: newClips,
                          createdAt: Date.now()
                      };
                      
                      // Auto-checkout pro novo branch
                      setClips(newClips);
                      setActiveBranchId(newBranch.id);
                      
                      return [...prevBranches, newBranch];
                  });
                  
                  setShowToast('Tarefa IA Concluída e Branch Criado!');
                  setTimeout(() => setShowToast(null), 3000);
               }, 1500);
            } else {
               setAiTasks(curr => curr.map(t => t.id === taskId ? {...t, progress: p} : t));
            }
        }, 800);

     }, 2000); // Fica ouvindo por 2s
  };

  const [phase, setPhase] = useState<Phase>('START_BUTTON');
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // NLE State
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [tracks, setTracks] = useState<Track[]>(INITIAL_TRACKS);
  const [clips, setClips] = useState<Clip[]>(INITIAL_CLIPS);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const [trackLabelWidth, setTrackLabelWidth] = useState(120);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(false);
  const trackContainerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  
  // Context Menu
  const [contextMenu, setContextMenu] = useState<{type: 'clip' | 'track' | 'visibility', targetId: string | number, x: number, y: number} | null>(null);
  
  // Tabs for Sidebar
  const [activeTab, setActiveTab] = useState<'info'|'props'|'anim'|'actions'|'projects'>('info');

  const [showToast, setShowToast] = useState<string | null>(null);
  const [draggingClip, setDraggingClip] = useState<{ id: string, startX: number, initialStartTime: number, initialTrackId?: number } | null>(null);
  const [showProperties, setShowProperties] = useState(false);
  const [selectionStart, setSelectionStart] = useState<number | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<number | null>(null);
  
  const TOTAL_DURATION = Math.max(...clips.map(c => getAbsoluteTime(c.id, clips) + c.duration), 60000);

  const audioRef = useRef<HTMLAudioElement>(null);
  const lastUpdateRef = useRef(Date.now());
  const requestRef = useRef<number | undefined>(undefined);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Telemetry Trace
  const telemetryRef = useRef<Array<{timestamp: number, event: string, data?: any}>>([]);
  const previousVideoRef = useRef<string | null>(null);

  const addTelemetry = (event: string, data?: any) => {
     telemetryRef.current.push({ timestamp: Math.floor(currentTime), event, data });
  };

  // Playback Loop
  const updateTime = () => {
    if (isPlaying && phase === 'SCRIPT') {
      const now = Date.now();
      const delta = now - lastUpdateRef.current;
      setCurrentTime(prev => {
        let next = prev + delta;
        if (next > TOTAL_DURATION) {
          next = TOTAL_DURATION;
          setIsPlaying(false);
        }
        return next;
      });
      lastUpdateRef.current = now;
    }
    requestRef.current = requestAnimationFrame(updateTime);
  };

  useEffect(() => {
    lastUpdateRef.current = Date.now();
    requestRef.current = requestAnimationFrame(updateTime);
    return () => cancelAnimationFrame(requestRef.current!);
  }, [isPlaying, phase]);

  
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
       if (isEditMode && isTimelineExpanded && editorRef.current && !editorRef.current.contains(e.target as Node)) {
           // check if we clicked on context menu or sidebar (they are outside editor but part of UI)
           const target = e.target as HTMLElement;
           if (!target.closest('.z-\\[100\\]') && !target.closest('.w-80')) {
               setIsTimelineExpanded(false);
           }
       }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditMode, isTimelineExpanded]);
// Sync Engine (Multi-Track)
  useEffect(() => {
    if (phase !== 'SCRIPT') return;
    
    const activeVideo = clips.find(c => { if(c.type !== 'visual') return false; const absStart = getAbsoluteTime(c.id); return currentTime >= absStart && currentTime < absStart + c.duration; });
    const activeAudio = clips.find(c => { if(c.type !== 'auditivo') return false; const absStart = getAbsoluteTime(c.id); return currentTime >= absStart && currentTime < absStart + c.duration; });
    
    // Video Telemetry
    if (activeVideo && activeVideo.id !== previousVideoRef.current) {
        if (previousVideoRef.current) addTelemetry(`[SCENE_${previousVideoRef.current.toUpperCase()}=END]`);
        addTelemetry(`[SCENE_${activeVideo.id.toUpperCase()}=BEGIN]`, { view: activeVideo.view });
        previousVideoRef.current = activeVideo.id;
    }

    // Audio Sync
    if (activeAudio?.audio && audioRef.current) {
        if (!audioRef.current.src.includes(activeAudio.audio)) {
           addTelemetry(`[AUDIO_${activeAudio.id.toUpperCase()}=LOAD]`);
           audioRef.current.src = activeAudio.audio;
           audioRef.current.currentTime = (currentTime - getAbsoluteTime(activeAudio.id)) / 1000;
           if (isPlaying) {
               audioRef.current.play().then(() => addTelemetry(`[AUDIO_${activeAudio.id.toUpperCase()}=PLAYING]`)).catch((e) => addTelemetry(`[AUDIO_${activeAudio.id.toUpperCase()}=ERROR]`, { error: e.message }));
           }
        } else {
           const expectedAudioTime = (currentTime - getAbsoluteTime(activeAudio.id)) / 1000;
           if (Math.abs(audioRef.current.currentTime - expectedAudioTime) > 0.5) {
              addTelemetry(`[AUDIO_${activeAudio.id.toUpperCase()}=SYNC_SEEK]`);
              audioRef.current.currentTime = expectedAudioTime;
           }
           if (isPlaying && audioRef.current.paused) {
               audioRef.current.play().then(() => addTelemetry(`[AUDIO_${activeAudio.id.toUpperCase()}=RESUME]`)).catch(()=>{});
           }
           if (!isPlaying && !audioRef.current.paused) {
               addTelemetry(`[AUDIO_${activeAudio.id.toUpperCase()}=PAUSE]`);
               audioRef.current.pause();
           }
        }
    } else if (audioRef.current && audioRef.current.src) {
        if (!audioRef.current.paused) {
            addTelemetry(`[AUDIO_STOP]`);
            audioRef.current.pause();
        }
        audioRef.current.src = '';
    }
  }, [currentTime, isPlaying, phase, clips]);

  // Drag and Drop Logic for Clips
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (draggingClip && timelineRef.current && trackContainerRef.current) {
        const rect = timelineRef.current.getBoundingClientRect();
        const deltaX = e.clientX - draggingClip.startX;
        const deltaMs = (deltaX / rect.width) * TOTAL_DURATION;
        const newStartTime = Math.max(0, draggingClip.initialStartTime + deltaMs);
        
        // Y-axis tracking — find which visible track the cursor is over
        const containerRect = trackContainerRef.current.getBoundingClientRect();
        const relativeY = e.clientY - containerRect.top + trackContainerRef.current.scrollTop;
        const trackIndex = Math.floor(relativeY / 32); // h-8 = 32px
        
        // Get visible tracks in display order
        const visibleTracks = [...tracks]
          .filter(t => !t.hidden && clips.some(c => c.trackId === t.id && (c.parentId || null) === currentGroupId))
          .sort((a,b) => { const _to: Record<string, number> = { interativo: 0, textual: 1, visual: 2, auditivo: 3 }; return (_to[a.type]??99) !== (_to[b.type]??99) ? (_to[a.type]??99) - (_to[b.type]??99) : a.order - b.order; });

        setClips(current => {
           const target = current.find(c => c.id === draggingClip.id);
           if (!target) return current;
           
           let newTrackId = target.trackId;
           // Only allow moving to same-type tracks
           if (trackIndex >= 0 && trackIndex < visibleTracks.length) {
               const hoverTrack = visibleTracks[trackIndex];
               if (hoverTrack.type === target.type) {
                   newTrackId = hoverTrack.id;
               }
           }
           
           if (target.startTime === newStartTime && target.trackId === newTrackId) return current;
           return current.map(c => c.id === target.id ? { ...c, startTime: newStartTime, trackId: newTrackId } : c);
        });
      }
    };

    const handleGlobalMouseUp = () => {
      if (draggingClip) {
         setClips(currentClips => {
            const target = currentClips.find(c => c.id === draggingClip.id);
            if (!target) return currentClips;
            
            const tAbs = getAbsoluteTime(target.id, currentClips);
            const hasCollision = currentClips.some(c => {
               if (c.id === target.id || c.trackId !== target.trackId) return false;
               const cAbs = getAbsoluteTime(c.id, currentClips);
               return (tAbs < cAbs + c.duration) && (tAbs + target.duration > cAbs);
            });
            
            if (!hasCollision) return currentClips;

            // Collision! Try to find another same-type track with room
            const sameTypeTracks = tracks.filter(t => {
               const parentTrack = tracks.find(pt => pt.id === target.trackId);
               return parentTrack && t.type === parentTrack.type;
            });

            for (const t of sameTypeTracks) {
                if (t.id === target.trackId) continue; // skip current track
                const collisionOnThisTrack = currentClips.some(c => {
                   if (c.id === target.id || c.trackId !== t.id) return false;
                   const cAbs = getAbsoluteTime(c.id, currentClips);
                   return (tAbs < cAbs + c.duration) && (tAbs + target.duration > cAbs);
                });
                if (!collisionOnThisTrack) {
                    return currentClips.map(c => c.id === target.id ? { ...c, trackId: t.id } : c);
                }
            }

            // No room anywhere → REVERT to original position and track
            return currentClips.map(c => c.id === target.id ? { ...c, startTime: draggingClip.initialStartTime, trackId: draggingClip.initialTrackId ?? target.trackId } : c);
         });
      }
      setDraggingClip(null);
    };
    
    if (draggingClip) {
      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [draggingClip, TOTAL_DURATION, tracks, clips, currentGroupId]);

  // Derived sorted tracks
  const sortedTracks = [...tracks].sort((a,b) => { const _to: Record<string, number> = { interativo: 0, textual: 1, visual: 2, auditivo: 3 }; return (_to[a.type]??99) !== (_to[b.type]??99) ? (_to[a.type]??99) - (_to[b.type]??99) : a.order - b.order; });

  const shiftClipAndSubsequent = (id: string, deltaMs: number) => {
    setClips(prev => {
      const target = prev.find(c => c.id === id);
      if (!target) return prev;
      return prev.map(c => {
        if (c.trackId === target.trackId && c.startTime >= target.startTime) {
          return { ...c, startTime: Math.max(0, c.startTime + deltaMs) };
        }
        return c;
      });
    });
  };

  const rippleEditDuration = (id: string, deltaMs: number) => {
    setClips(prev => {
      const target = prev.find(c => c.id === id);
      if (!target) return prev;
      const newDuration = Math.max(100, target.duration + deltaMs);
      const actualDelta = newDuration - target.duration;
      return prev.map(c => {
        if (c.id === id) {
          return { ...c, duration: newDuration };
        } else if (c.trackId === target.trackId && c.startTime > target.startTime) {
          return { ...c, startTime: Math.max(0, c.startTime + actualDelta) };
        }
        return c;
      });
    });
  };

  const handleTimeRulerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickTime = (clickX / rect.width) * TOTAL_DURATION;

    if (e.ctrlKey) {
        if (selectionStart === null || (selectionStart !== null && selectionEnd !== null)) {
            setSelectionStart(clickTime);
            setSelectionEnd(null);
        } else {
            setSelectionEnd(clickTime);
        }
    } else {
        setCurrentTime(clickTime);
        if (audioRef.current) {
            audioRef.current.currentTime = clickTime / 1000;
        }
    }
  };

  const handleStart = () => {
    addTelemetry('[PHASE=START_BUTTON_CLICKED]');
    addTelemetry('[PHASE=SCRIPT_START]');
    setPhase('SCRIPT');
    setCurrentTime(0);
    setIsPlaying(true);
  };

  const handleSkip = () => {
    addTelemetry('[USER_ACTION=SKIP_SIMULATION]');
    if (audioRef.current) audioRef.current.pause();
    setIsPlaying(false);
    setPhase('START_BUTTON');
  };

  const handleTimelineInteraction = (e: React.MouseEvent | React.TouchEvent, isDragging: boolean) => {
    if (!timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const adjustedX = clientX - rect.left - trackLabelWidth;
    const usableWidth = rect.width - trackLabelWidth;
    if (adjustedX < 0) return; // Ignore clicks on label area
    const percentage = Math.max(0, Math.min(1, adjustedX / usableWidth));
    const newTime = percentage * TOTAL_DURATION;

    setCurrentTime(newTime);
    if (audioRef.current && !isDragging) {
        audioRef.current.currentTime = newTime / 1000;
    }
  };

  const updateClip = (id: string, updates: Partial<Clip>, ripple: boolean) => {
     setClips(prev => {
        const idx = prev.findIndex(c => c.id === id);
        if (idx === -1) return prev;
        const target = prev[idx];
        const newClips = [...prev];
        const timeDiff = (updates.startTime !== undefined) ? updates.startTime - target.startTime : 0;
        
        newClips[idx] = { ...target, ...updates };
        
        if (ripple && timeDiff !== 0) {
           for (let i = 0; i < newClips.length; i++) {
              if (i !== idx && newClips[i].trackId === target.trackId && newClips[i].startTime >= target.startTime) {
                  newClips[i] = { ...newClips[i], startTime: newClips[i].startTime + timeDiff };
              }
           }
        }
        return newClips;
     });
  };

  const generateExportData = () => {
    const exportData: any = {
        export_type: "TIMELINE_SNAPSHOT",
        timeline_state: { current_time_ms: Math.floor(currentTime), total_duration_ms: Math.floor(TOTAL_DURATION), is_playing: isPlaying },
    };
    
    if (selectionStart !== null && selectionEnd !== null && Math.abs(selectionEnd - selectionStart) > 100) {
       const start = Math.min(selectionStart, selectionEnd);
       const end = Math.max(selectionStart, selectionEnd);
       exportData.export_type = "REGION_EXPORT";
       exportData.selection_region = { start: Math.floor(start), end: Math.floor(end) };
       
       exportData.telemetry = {
           past: telemetryRef.current.filter(t => t.timestamp < start),
           selected: telemetryRef.current.filter(t => t.timestamp >= start && t.timestamp <= end),
           future: telemetryRef.current.filter(t => t.timestamp > end)
       };
       exportData.clips = {
           past: clips.filter(c => c.startTime + c.duration < start),
           selected: clips.filter(c => (c.startTime >= start && c.startTime <= end) || (c.startTime < start && c.startTime + c.duration > start)),
           future: clips.filter(c => c.startTime > end)
       };
    } else {
       const cur = Math.floor(currentTime);
       exportData.telemetry = {
           past: telemetryRef.current.filter(t => t.timestamp < cur),
           current: telemetryRef.current.filter(t => Math.abs(t.timestamp - cur) <= 100),
           future: telemetryRef.current.filter(t => t.timestamp > cur + 100)
       };
       exportData.clips = {
           past: clips.filter(c => c.startTime + c.duration < cur),
           current: clips.filter(c => c.startTime <= cur && c.startTime + c.duration >= cur),
           future: clips.filter(c => c.startTime > cur)
       };
    }
    return exportData;
  };

  const copyStateData = () => {
    navigator.clipboard.writeText(JSON.stringify(generateExportData(), null, 2))
       .then(() => setShowToast((selectionStart !== null && selectionEnd !== null) ? "Região Copiada!" : "Estado Atual Copiado!"))
       .catch(() => setShowToast("Erro ao copiar. Tente Download."));
    setTimeout(() => setShowToast(null), 3000);
  };

  const downloadStateData = () => {
    const blob = new Blob([JSON.stringify(generateExportData(), null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `alunoconectado_telemetry_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowToast("Download Iniciado!");
    setTimeout(() => setShowToast(null), 3000);
  };

  const handleInteractiveAction = (actionLabel: string) => {
    addTelemetry(`[USER_ACTION=${actionLabel.toUpperCase()}]`);
    setShowToast(`Ação Registrada: ${actionLabel}`);
    setTimeout(() => {
      setShowToast(null);
      addTelemetry('[PHASE=END]');
      setPhase('START_BUTTON');
    }, 3000);
  };

  // === RENDER VIEWS ===
  const renderSceneLogoFull = () => (
    <div className="flex flex-col items-center justify-center text-center relative z-10 w-full h-full">
      <div className="flex items-center gap-6 mb-8 animate-slide-up">
         <img src="/icons/logo-shield.svg" className="w-28 h-28 md:w-36 md:h-36 drop-shadow-[0_0_40px_rgba(168,85,247,0.6)]" />
         <div className="text-left animate-fade-in-left">
           <h2 className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-100 to-purple-400">Aluno</h2>
           <h2 className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-emerald-400">Conectado</h2>
         </div>
      </div>
    </div>
  );

  const renderSceneLogoShield = () => (
    <div className="flex flex-col items-center justify-center text-center relative z-10 w-full h-full">
      <div className="flex items-center gap-6 mb-8 transform -translate-y-[60px] transition-transform duration-1000">
         <img src="/icons/logo-shield.svg" className="w-28 h-28 md:w-36 md:h-36 drop-shadow-[0_0_40px_rgba(168,85,247,0.6)]" />
      </div>
    </div>
  );

  const renderImpactText = (text: string) => (
    <motion.div className="flex items-center justify-center h-full w-full">
       <h1 className="text-7xl md:text-9xl font-black text-white tracking-tighter uppercase drop-shadow-[0_0_30px_rgba(255,255,255,0.5)]">{text}</h1>
    </motion.div>
  );

  const renderSceneIntroEliete = () => (
    <div className="w-full max-w-6xl mx-auto h-[600px] relative flex justify-center items-center">
      {/* CORREÇÃO DO ALINHAMENTO DA FOTO DA ELIETE */}
      <img src="/images/story/eliete_intro.png" className="w-full h-full object-cover rounded-[3rem] shadow-2xl border border-purple-500/30 animate-fade-in-left" style={{ objectPosition: 'top' }} />
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent rounded-[3rem] p-12 flex flex-col justify-end">
         <h2 className="text-6xl font-black text-white">ELIETE</h2>
         <p className="text-3xl text-purple-200">Mãe, Empresária, Sempre com Pressa.</p>
      </div>
    </div>
  );

  const renderSceneIntroGabriel = () => (
    <div className="w-full max-w-6xl mx-auto h-[600px] relative flex justify-center items-center">
      <img src="/images/story/gabriel_intro.png" className="w-full h-full object-cover rounded-[3rem] shadow-2xl border border-blue-500/30 animate-fade-in" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent rounded-[3rem] p-12 flex flex-col justify-end">
         <h2 className="text-6xl font-black text-white">GABRIEL, 12 ANOS</h2>
         <p className="text-3xl text-blue-200">Preguiçoso, Enrolado e com TDAH.</p>
      </div>
    </div>
  );

  const renderSceneVan = () => (
    <div className="w-full max-w-6xl mx-auto h-[600px] rounded-[3rem] overflow-hidden shadow-2xl relative border border-emerald-500/30 flex bg-[#162a1f]">
      <div className="w-1/2 h-full relative">
         <img src="/images/story/eliete_intro.png" className="absolute inset-0 w-full h-full object-cover opacity-60" style={{ objectPosition: 'top' }} />
         <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#162a1f]"></div>
      </div>
      <div className="w-1/2 h-full flex items-center justify-center p-8 z-10">
         <div className="w-80 h-[500px] bg-white rounded-[2.5rem] p-4 shadow-[0_30px_60px_rgba(0,0,0,0.5)] border-[8px] border-slate-800 flex flex-col animate-slide-up">
            <div className="w-full bg-emerald-50 rounded-2xl p-6 mt-12 shadow-sm border border-emerald-100">
               <div className="flex items-center gap-3 mb-4">
                 <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center"><CheckCircle2 className="text-white" size={20}/></div>
                 <h4 className="font-bold text-slate-800">Aluno Conectado</h4>
               </div>
               <p className="text-slate-600 font-medium">Gabriel entrou na Van Escolar.</p>
               <span className="text-xs text-slate-400 mt-4 block">Agora mesmo</span>
            </div>
         </div>
      </div>
    </div>
  );

  const renderScenePortaria = () => (
    <div className="w-full max-w-6xl mx-auto h-[600px] rounded-[3rem] overflow-hidden shadow-2xl relative border border-orange-500/30 flex flex-col items-center justify-center bg-[#2b1f13]">
      <div className="w-80 bg-white rounded-[2.5rem] p-4 shadow-[0_0_100px_rgba(249,115,22,0.3)] border-[8px] border-slate-800 flex flex-col relative animate-pop-in">
        <div className="w-full bg-emerald-50 rounded-2xl p-6 mt-12 shadow-sm border border-emerald-100 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center"><CheckCircle2 className="text-white" size={20}/></div>
              <h4 className="font-bold text-slate-800">Aluno Conectado</h4>
            </div>
            <p className="text-slate-600 font-medium">Gabriel passou na Catraca Principal.</p>
            <span className="text-xs text-slate-400 mt-4 block">06:50</span>
        </div>
        <div className="absolute -bottom-8 -right-8 bg-orange-500 text-white p-6 rounded-2xl shadow-2xl w-[110%] rotate-[-5deg] border-4 border-[#2b1f13] animate-bounce-in">
           <div className="flex items-center gap-3">
             <AlertTriangle size={32} />
             <div><h4 className="font-black uppercase text-xl">Atenção</h4><p className="font-medium text-sm">Limite 07:05</p></div>
           </div>
        </div>
      </div>
    </div>
  );

  const renderSceneSalaAula = (step: number) => (
    <div className={`w-full max-w-6xl mx-auto h-[600px] rounded-[3rem] overflow-hidden shadow-2xl relative border flex transition-colors duration-1000 ${step === 0 ? 'border-purple-500/30 bg-[#160b2b]' : 'border-red-500/50 bg-[#241212]'}`}>
       {step === 0 ? (
         <div className="w-full h-full relative animate-fade-in">
            <img src="/images/story/teacher_math.png" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-12">
               <h3 className="text-5xl font-black text-white">Professora</h3><p className="text-3xl text-purple-200">A aula começa. O diário é aberto.</p>
            </div>
         </div>
       ) : (
         <div className="w-full h-full relative flex items-center justify-center p-12 animate-fade-in">
            <img src="/images/story/teacher_math.png" className="absolute inset-0 w-full h-full object-cover opacity-20 grayscale" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#241212] via-[#241212]/80 to-[#241212]"></div>
            <div className="relative z-10 flex flex-col items-center text-center">
              <XCircle size={120} className="text-red-500 mb-8" />
              <h3 className="text-6xl font-black text-red-500 mb-4 uppercase tracking-tighter">Diário Fechado</h3>
              <div className="bg-red-600 text-white px-8 py-4 rounded-xl text-4xl font-black shadow-2xl">Gabriel AUSENTE</div>
            </div>
         </div>
       )}
    </div>
  );

  const renderSceneSistemaAge = () => (
    <div className="w-full max-w-6xl mx-auto h-[600px] rounded-[3rem] overflow-hidden shadow-2xl relative border border-blue-500/50 bg-[#0f172a] p-12">
      <h3 className="text-5xl font-black text-blue-400 text-center mb-12 tracking-tighter uppercase">O Aluno Conectado Age</h3>
      <div className="flex justify-between items-center gap-4 mb-12">
         {[{ name: 'Odete', role: 'Secretaria', img: '/images/story/odete_secretary.png' }, { name: 'Huguloi', role: 'Coord.', img: '/images/story/huguloi_coordinator.png' }, { name: 'Giuliun', role: 'Direção', img: '/images/story/giuliun_director.png' }].map((p, i) => (
           <div key={i} className="flex flex-col items-center w-1/3 animate-slide-up" style={{ animationDelay: `${i * 0.4}s` }}>
              <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-blue-500 mb-4 shadow-[0_0_30px_rgba(59,130,246,0.3)] relative">
                 <img src={p.img} className="w-full h-full object-cover" /><div className="absolute top-0 right-0 bg-red-500 w-6 h-6 rounded-full border-2 border-slate-900 animate-pulse"></div>
              </div>
              <h4 className="text-white font-bold text-2xl">{p.name}</h4><span className="text-blue-300">{p.role}</span>
           </div>
         ))}
      </div>
    </div>
  );

  const renderSceneDesfecho = () => (
    <div className="w-full max-w-6xl mx-auto h-[600px] rounded-[3rem] overflow-hidden shadow-2xl relative border border-emerald-500/50 bg-[#12241d] flex flex-col md:flex-row items-stretch p-8 gap-8">
       <div className="w-1/2 flex flex-col items-center">
         <div className="w-full h-64 rounded-3xl overflow-hidden mb-6 relative">
            <img src="/images/story/giuliun_director.png" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6"><h3 className="text-3xl font-black text-white">Diretor Giuliun</h3></div>
         </div>
       </div>
       <div className="w-1/2 flex flex-col items-center">
          <div className="w-full h-[400px] bg-white rounded-[2.5rem] p-4 shadow-2xl border-[8px] border-slate-800 flex flex-col">
             <div className="w-full bg-red-50 rounded-2xl p-6 mt-4 shadow-sm border border-red-100 flex-1 flex flex-col">
                 <div className="flex items-center gap-3 mb-4"><div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center"><AlertCircle className="text-white" size={20}/></div><h4 className="font-bold text-slate-800">Advertência</h4></div>
                 <div className="mt-auto space-y-3">
                    <button onClick={() => handleInteractiveAction("CONCORDO_CIENTE")} className="w-full bg-emerald-500 text-white py-4 rounded-xl font-black shadow-lg hover:scale-105 transition-all text-lg">CONCORDO E CIENTE</button>
                    <button onClick={() => handleInteractiveAction("CONTESTO")} className="w-full bg-white text-slate-600 py-3 rounded-xl font-bold border border-slate-300 hover:bg-slate-50 transition-colors">CONTESTO</button>
                 </div>
             </div>
          </div>
       </div>
    </div>
  );

  // === RENDER ENGINE ===
  const activeVideo = clips.find(c => c.type === 'visual' && currentTime >= getAbsoluteTime(c.id) && currentTime < getAbsoluteTime(c.id) + c.duration);
  const activeText = clips.find(c => c.type === 'textual' && currentTime >= getAbsoluteTime(c.id) && currentTime < getAbsoluteTime(c.id) + c.duration);

  return (
    <PremiumBackground performanceMode="lite" className="h-screen w-screen font-sans relative selection:bg-purple-500/30 overflow-hidden flex flex-col">
      <audio ref={audioRef} className="hidden" />
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fadeInLeft { from { opacity: 0; transform: translateX(-50px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes popIn { from { opacity: 0; transform: scale(0.8); } to { opacity: 1; transform: scale(1); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes bounceIn { 0% { opacity: 0; transform: scale(0); } 50% { transform: scale(1.1); } 100% { opacity: 1; transform: scale(1); } }
        .animate-fade-in { animation: fadeIn 1s ease-out forwards; }
        .animate-fade-in-fast { animation: fadeIn 0.3s ease-out forwards; }
        .animate-fade-in-left { animation: fadeInLeft 1s ease-out forwards; }
        .animate-pop-in { animation: popIn 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
        .animate-slide-up { animation: slideUp 0.8s ease-out forwards; }
        .animate-bounce-in { animation: bounceIn 0.6s ease-out forwards; }
      `}</style>

      {/* Main Content Area */}
      <div className="flex-1 w-full flex overflow-hidden relative" onMouseDown={() => setShowProperties(false)}>

      {/* TASK DASHBOARD */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 flex flex-col gap-2 z-[110] pointer-events-none">
        <AnimatePresence>
          {aiTasks.map(task => {
              // Calculando cor pela eficiência
              let borderColor = 'border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.6)]';
              let progressColor = 'bg-blue-500';
              let iconColor = 'text-blue-400';
              
              if (task.efficiency >= 80) {
                 borderColor = 'border-green-400 shadow-[0_0_20px_rgba(74,222,128,0.8)]'; // Verde Neon
                 progressColor = 'bg-green-400';
                 iconColor = 'text-green-400';
              } else if (task.efficiency >= 40) {
                 borderColor = 'border-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.6)]';
                 progressColor = 'bg-orange-500';
                 iconColor = 'text-orange-400';
              } else {
                 borderColor = 'border-red-600 shadow-[0_0_15px_rgba(220,38,38,0.8)]';
                 progressColor = 'bg-red-600';
                 iconColor = 'text-red-500';
              }

              return (
                 <motion.div key={task.id} initial={{y: -50, opacity: 0}} animate={{y: 0, opacity: 1}} exit={{y: -50, opacity: 0}} className={`bg-slate-900/90 backdrop-blur-md rounded-lg p-3 border-2 ${borderColor} flex items-center gap-4 min-w-[300px]`}>
                    <div className={`animate-spin ${iconColor}`}><Settings size={20} /></div>
                    <div className="flex-1">
                       <div className="flex justify-between items-end mb-1">
                          <span className="text-white font-bold text-xs">{task.name}</span>
                          <span className={`text-[10px] font-black ${iconColor}`}>{task.efficiency}% Eficiência</span>
                       </div>
                       <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div className={`h-full ${progressColor} transition-all duration-300 ease-out`} style={{width: `${task.progress}%`}}></div>
                       </div>
                    </div>
                 </motion.div>
              );
          })}
        </AnimatePresence>
      </div>

      {/* GEMINI LISTENING OVERLAY */}
      <AnimatePresence>
         {isListening && (
            <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm pointer-events-none">
                <div className="bg-slate-900 border-2 border-purple-500 shadow-[0_0_50px_rgba(168,85,247,0.8)] p-8 rounded-full flex flex-col items-center gap-4 animate-pulse">
                   <div className="w-16 h-16 bg-gradient-to-tr from-purple-600 to-blue-500 rounded-full flex items-center justify-center animate-spin">
                      <Sparkles className="text-white" size={32} />
                   </div>
                   <h2 className="text-white font-black text-2xl">Ouvindo...</h2>
                </div>
            </motion.div>
         )}
      </AnimatePresence>

        <div className="flex-1 w-full h-full relative z-10 flex items-center justify-center p-6">
          <AnimatePresence mode="wait">
            {phase === 'START_BUTTON' && (
              <motion.div key="start" exit={{opacity: 0}} className="min-h-screen flex items-center justify-center text-center">
                <button onClick={handleStart} className="bg-white text-purple-950 px-12 py-6 rounded-full font-black text-3xl hover:scale-105 transition-all"><Volume2 size={32} className="inline mr-4 text-purple-600 animate-pulse" />Iniciar Experiência</button>
              </motion.div>
            )}
            {phase === 'SCRIPT' && (activeVideo || activeText) && (
              <div key={activeVideo?.startTime ?? activeText?.startTime} className="w-full flex items-center justify-center h-full animate-fade-in-fast">
                {activeText && activeText.view === 'IMPACT_TEXT' && renderImpactText(activeText.text!)}
                {activeVideo?.view === 'SCENE_LOGO_FULL' && renderSceneLogoFull()}
                {activeVideo?.view === 'SCENE_LOGO_SHIELD' && renderSceneLogoShield()}
                {activeVideo?.view === 'SCENE_INTRO_ELIETE' && renderSceneIntroEliete()}
                {activeVideo?.view === 'SCENE_INTRO_GABRIEL' && renderSceneIntroGabriel()}
                {activeVideo?.view === 'SCENE_VAN' && renderSceneVan()}
                {activeVideo?.view === 'SCENE_PORTARIA' && renderScenePortaria()}
                {activeVideo?.view === 'SCENE_SALA_AULA' && renderSceneSalaAula(activeVideo.step!)}
                {activeVideo?.view === 'SCENE_SISTEMA_AGE' && renderSceneSistemaAge()}
                {activeVideo?.view === 'SCENE_DESFECHO' && renderSceneDesfecho()}
              </div>
            )}
          </AnimatePresence>
        </div>

        
        {/* Hover trigger zone */}
        {isEditMode && selectedClipId && !showProperties && (
            <div className="absolute right-0 top-0 bottom-0 w-16 z-40" onMouseEnter={() => setShowProperties(true)}></div>
        )}

        {/* PROPERTIES SIDEBAR (EDIT MODE) */}
        <AnimatePresence>
          {isEditMode && (selectedClipId || activeTab === 'projects') && (
            <motion.div initial={{x: 350, opacity: 0}} animate={{x: showProperties ? 0 : 350, opacity: showProperties ? 1 : 0}} className="absolute right-4 top-4 bottom-4 w-80 bg-[#0f172a]/95 backdrop-blur-md border border-slate-700/50 z-[100] shadow-2xl rounded-2xl flex flex-col overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center p-4 border-b border-slate-700/50 bg-slate-800/30">
                 <h3 className="text-sm font-bold text-white flex items-center gap-2"><Settings size={16} className="text-purple-400"/> {selectedClipId ? 'Propriedades' : 'Projetos'}</h3>
                 <button onClick={() => {setShowProperties(false); setSelectedClipId(null);}} className="text-slate-400 hover:text-white"><X size={14}/></button>
              </div>

              {selectedClipId && (() => {
                const c = clips.find(x => x.id === selectedClipId);
                if(!c) return null;
                return (
                  <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Preview Box */}
                    <div className="h-32 bg-slate-900 border-b border-slate-700/50 flex items-center justify-center relative p-2">
                       <span className="text-slate-600 font-black text-4xl opacity-50 absolute">PREVIEW</span>
                       {c.text && <div className="z-10 text-white font-bold text-center drop-shadow-md">{c.text}</div>}
                    </div>

                    {/* Tabs Header */}
                    <div className="flex border-b border-slate-700/50 bg-slate-800/20 px-2 pt-2 gap-1 overflow-x-auto no-scrollbar shrink-0">
                      {['info', 'props', 'anim', 'actions'].map(tab => (
                        <button key={tab} onClick={() => setActiveTab(tab as any)} className={`px-3 py-1.5 text-[10px] font-bold rounded-t-lg transition-colors ${activeTab === tab ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
                          {tab.toUpperCase()}
                        </button>
                      ))}
                    </div>

                    {/* Tab Content */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">
                      {activeTab === 'info' && (
                        <>
                          <div>
                            <label className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">ID / Texto</label>
                            <input value={c.text || c.id} onChange={(e) => updateClip(c.id, { text: e.target.value }, false)} className="w-full bg-slate-800 text-white p-2 rounded border border-slate-700 focus:border-purple-500 outline-none text-xs mt-1" />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-400 uppercase font-bold tracking-wider flex items-center gap-1"><Sparkles size={10} className="text-amber-400"/> Prompt IA (Gerador)</label>
                            <textarea placeholder="Ex: Gere uma introdução animada..." className="w-full h-24 bg-slate-800 text-white p-2 rounded border border-slate-700 focus:border-purple-500 outline-none text-xs mt-1 custom-scrollbar" />
                            <button className="w-full mt-2 bg-purple-600 hover:bg-purple-500 text-white font-bold text-[10px] py-1.5 rounded flex items-center justify-center gap-2"><Sparkles size={12}/> REGERAR COM IA</button>
                          </div>
                        </>
                      )}
                      {activeTab === 'props' && (
                        <>
                          <div>
                            <label className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Início (ms)</label>
                            <div className="flex gap-1 mt-1">
                              <button onClick={() => shiftClipAndSubsequent(c.id, -100)} className="bg-slate-800 hover:bg-slate-700 text-slate-300 p-1.5 rounded" title="Mover a partir daqui -100ms"><ChevronsLeft size={14}/></button>
                              <button onClick={() => updateClip(c.id, { startTime: Math.max(0, c.startTime - 100) }, false)} className="bg-slate-800 hover:bg-slate-700 text-slate-300 p-1.5 rounded"><ArrowLeft size={14}/></button>
                              <input type="number" value={Math.floor(c.startTime)} onChange={(e) => updateClip(c.id, { startTime: Number(e.target.value) }, false)} className="flex-1 min-w-0 bg-slate-800 text-white p-1.5 rounded border border-slate-700 outline-none text-xs text-center" />
                              <button onClick={() => updateClip(c.id, { startTime: c.startTime + 100 }, false)} className="bg-slate-800 hover:bg-slate-700 text-slate-300 p-1.5 rounded"><ArrowRight size={14}/></button>
                              <button onClick={() => shiftClipAndSubsequent(c.id, 100)} className="bg-slate-800 hover:bg-slate-700 text-slate-300 p-1.5 rounded"><ChevronsRight size={14}/></button>
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Duração (ms)</label>
                            <div className="flex gap-1 mt-1">
                              <button onClick={() => rippleEditDuration(c.id, -100)} className="bg-slate-800 hover:bg-slate-700 text-slate-300 p-1.5 rounded"><ChevronsLeft size={14}/></button>
                              <button onClick={() => updateClip(c.id, { duration: Math.max(100, c.duration - 100) }, false)} className="bg-slate-800 hover:bg-slate-700 text-slate-300 p-1.5 rounded"><ArrowLeft size={14}/></button>
                              <input type="number" value={Math.floor(c.duration)} onChange={(e) => updateClip(c.id, { duration: Number(e.target.value) }, false)} className="flex-1 min-w-0 bg-slate-800 text-white p-1.5 rounded border border-slate-700 outline-none text-xs text-center" />
                              <button onClick={() => updateClip(c.id, { duration: c.duration + 100 }, false)} className="bg-slate-800 hover:bg-slate-700 text-slate-300 p-1.5 rounded"><ArrowRight size={14}/></button>
                              <button onClick={() => rippleEditDuration(c.id, 100)} className="bg-slate-800 hover:bg-slate-700 text-slate-300 p-1.5 rounded"><ChevronsRight size={14}/></button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}

              {!selectedClipId && activeTab === 'projects' && (
                  <div className="flex-1 p-4 flex flex-col gap-4 overflow-y-auto custom-scrollbar">
                     <button className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2 rounded-lg flex items-center justify-center gap-2"><Plus size={16}/> Novo Projeto</button>
                     <div className="space-y-2">
                        {projects.map(p => (
                           <div key={p.id} className="bg-slate-800 p-3 rounded-lg border border-slate-700 cursor-pointer hover:border-purple-500 transition-colors">
                              <h4 className="text-white font-bold text-sm">{p.name}</h4>
                              <p className="text-slate-400 text-xs">{p.snapshots.length} Snapshots</p>
                           </div>
                        ))}
                        {projects.length === 0 && <p className="text-slate-500 text-center text-xs mt-4">Nenhum projeto local.</p>}
                     </div>
                  </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* CONTEXT MENU */}
      <AnimatePresence>
        {contextMenu && (
           <motion.div initial={{opacity:0, scale:0.95}} animate={{opacity:1, scale:1}} exit={{opacity:0, scale:0.95}} className="fixed bg-slate-800 border border-slate-700 shadow-2xl rounded-lg py-1 z-[100] w-48" style={{left: contextMenu.x, top: contextMenu.y}} onMouseLeave={() => setContextMenu(null)}>
              {contextMenu.type === 'clip' && (
                  <>
                      <button className="w-full px-4 py-2 text-left text-xs text-white hover:bg-slate-700 flex items-center gap-2" onClick={() => { setSelectedClipId(contextMenu.targetId as string); setShowProperties(true); setActiveTab('info'); setContextMenu(null); }}><Edit3 size={14}/> Editar</button>
                      <button className="w-full px-4 py-2 text-left text-xs text-white hover:bg-slate-700 flex items-center gap-2" onClick={() => { 
                          const c = clips.find(x => x.id === contextMenu.targetId); 
                          if(c) setClips([...clips, {...c, id: c.id+'_copy', startTime: c.startTime + c.duration}]); 
                          setContextMenu(null); 
                      }}><Copy size={14}/> Duplicar</button>
                      <button className="w-full px-4 py-2 text-left text-xs text-white hover:bg-slate-700 flex items-center gap-2" onClick={() => { 
                          // Create Group 
                          const c = clips.find(x => x.id === contextMenu.targetId); 
                          if(c) {
                              const groupId = 'group_' + Date.now();
                              const newGroupClip: Clip = { id: groupId, type: 'GROUP', trackId: 0, startTime: c.startTime, duration: c.duration, text: 'Novo Grupo' };
                              setClips([...clips.map(x => x.id === c.id ? {...x, parentId: groupId} : x), newGroupClip]);
                          }
                          setContextMenu(null); 
                      }}><FolderOpen size={14}/> Agrupar</button>
                      <button className="w-full px-4 py-2 text-left text-xs text-red-400 hover:bg-slate-700 flex items-center gap-2" onClick={() => { setClips(clips.filter(x => x.id !== contextMenu.targetId && x.parentId !== contextMenu.targetId)); setContextMenu(null); }}><XCircle size={14}/> Apagar</button>
                  </>
              )}
              {contextMenu.type === 'track' && (
                  <>
                      <button className="w-full px-4 py-2 text-left text-xs text-white hover:bg-slate-700 flex items-center gap-2" onClick={() => { 
                          const n = prompt('Nome da faixa'); 
                          if(n) setTracks(tracks.map(t => t.id === contextMenu.targetId ? {...t, name: n} : t)); 
                          setContextMenu(null); 
                      }}><Edit3 size={14}/> Renomear</button>
                      {Number(contextMenu.targetId) > 3 && (
                          <button className="w-full px-4 py-2 text-left text-xs text-red-400 hover:bg-slate-700 flex items-center gap-2" onClick={() => { 
                              setTracks(tracks.filter(t => t.id !== contextMenu.targetId)); 
                              setContextMenu(null); 
                          }}><XCircle size={14}/> Excluir Faixa</button>
                      )}
                  </>
              )}
           </motion.div>
        )}
      </AnimatePresence>

            {/* TIMELINE / NLE EDITOR */}
      {phase === 'SCRIPT' && (
        <div 
          ref={editorRef}
          
          className={`absolute bottom-0 left-0 right-0 bg-[#0f172a]/95 backdrop-blur-xl border-t border-slate-700/80 p-1 z-50 flex flex-col shrink-0 transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] shadow-[0_-20px_50px_rgba(0,0,0,0.6)] ${isEditMode ? 'h-[200px]' : 'h-10'}`}
        >
          <div className="flex items-center gap-2 px-2 h-6 shrink-0">
            <button onClick={() => setIsPlaying(!isPlaying)} className="w-8 h-8 bg-purple-600 hover:bg-purple-500 rounded-full flex items-center justify-center text-white shadow-lg shrink-0 transition-colors border border-purple-500/50">
              {isPlaying ? <Pause size={12} fill="currentColor"/> : <Play size={12} fill="currentColor" className="ml-0.5"/>}
            </button>
            <div className="text-slate-400 font-mono text-[10px] w-8 shrink-0">{Math.floor(currentTime / 1000)}s</div>
            
            {isEditMode && (
                <div className="flex items-center gap-1 ml-2">
                    <button onClick={copyStateData} className="w-6 h-6 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded flex items-center justify-center border border-slate-700" title="Exportar Estado"><Copy size={12} /></button>
                    <button onClick={downloadStateData} className="w-6 h-6 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded flex items-center justify-center border border-slate-700" title="Download JSON"><Download size={12} /></button>
                    <button onClick={() => setIsEditMode(false)} className="w-6 h-6 bg-emerald-600 hover:bg-emerald-500 text-white rounded flex items-center justify-center border border-emerald-500" title="Sair do Modo Edição"><CheckCircle2 size={12} /></button>
                </div>
            )}

            {/* BRANCH TABS */}
            {isEditMode && (
               <div className="flex items-center gap-1 ml-4 overflow-x-auto no-scrollbar shrink-0 border-l border-slate-700 pl-4">
                  {/* BREADCRUMBS */}
                  <div className="flex items-center text-[10px] font-bold mr-4 bg-slate-800 rounded px-2 py-1 border border-slate-700">
                     <span className="cursor-pointer hover:text-white text-slate-400 transition-colors" onClick={() => setCurrentGroupId(null)}>Main</span>
                     {currentGroupId && (
                         <>
                           <span className="text-slate-600 mx-1">/</span>
                           <span className="text-purple-400">{clips.find(c => c.id === currentGroupId)?.text || currentGroupId}</span>
                         </>
                     )}
                  </div>
                  <span className="text-[10px] text-slate-500 font-bold uppercase mr-1">Branches:</span>
                  {branches.map(b => (
                     <button 
                        key={b.id} 
                        onClick={() => {
                           setActiveBranchId(b.id);
                           setClips(b.clips);
                           addTelemetry(`[BRANCH_CHECKOUT=${b.name}]`);
                        }}
                        className={`px-2 py-1 text-[10px] font-bold rounded-md transition-all border ${activeBranchId === b.id ? 'bg-purple-600/30 border-purple-500 text-white shadow-[0_0_10px_rgba(168,85,247,0.4)]' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'}`}
                     >
                        {b.name}
                     </button>
                  ))}
               </div>
            )}


            <div className="flex-1 flex justify-end gap-2">
              {!isEditMode && (
                 <button onClick={() => setIsEditMode(true)} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold transition-colors bg-slate-800 text-slate-300 hover:bg-slate-700" title="Modo Edição">
                    <Edit3 size={12} />
                 </button>
              )}
              {isEditMode && (
                 <button onClick={() => {setShowProperties(true); setActiveTab('projects'); setSelectedClipId(null);}} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold transition-colors bg-slate-800 text-slate-300 hover:bg-slate-700" title="Projetos">
                    <FolderOpen size={12} /> Projetos
                 </button>
              )}
            </div>
          </div>

          {!isEditMode && (
            <div ref={timelineRef} className="flex-1 mt-1 group relative cursor-pointer flex items-center px-2" onMouseDown={(e) => handleTimelineInteraction(e, false)} onMouseMove={(e) => e.buttons === 1 && handleTimelineInteraction(e, true)}>
              <div className="w-full h-1.5 bg-slate-800 rounded-full relative pointer-events-none">
                <div className="h-full bg-purple-500 relative z-20 rounded-full" style={{ width: `${(currentTime / TOTAL_DURATION) * 100}%` }} />
              </div>
              <div className="absolute w-3 h-3 bg-white rounded-full shadow-[0_0_10px_rgba(168,85,247,1)] z-30 transform -translate-x-1/2 pointer-events-none group-hover:scale-125" style={{ left: `calc(${(currentTime / TOTAL_DURATION) * 100}% + 8px)` }} />
            </div>
          )}

          {isEditMode && (
            <div className="flex-1 flex flex-col overflow-hidden relative mt-2 border border-slate-700/50 rounded-lg bg-[#0b101e] shadow-inner">
              {/* Ruler Top Area */}
              <div className="h-4 w-full relative bg-slate-800/80 border-b border-slate-700 flex items-end cursor-crosshair" ref={timelineRef} onMouseDown={(e) => handleTimelineInteraction(e, false)} onMouseMove={(e) => e.buttons === 1 && handleTimelineInteraction(e, true)}>
                  <div className="absolute top-0 bottom-0 right-0 pointer-events-none overflow-hidden" style={{left: trackLabelWidth, background: 'repeating-linear-gradient(to right, transparent, transparent 4.9%, #334155 4.9%, #334155 5%)'}}></div>
                  {selectionStart !== null && (
                    <div className="absolute top-0 bottom-0 bg-blue-500/20 border-x border-blue-500 z-10 pointer-events-none" 
                         style={{ left: `calc(${trackLabelWidth * (1 - Math.min(selectionStart, selectionEnd ?? currentTime) / TOTAL_DURATION)}px + ${(Math.min(selectionStart, selectionEnd ?? currentTime) / TOTAL_DURATION) * 100}%)`, width: `calc(${(Math.abs((selectionEnd ?? currentTime) - selectionStart) / TOTAL_DURATION) * 100}% - ${trackLabelWidth * (Math.abs((selectionEnd ?? currentTime) - selectionStart) / TOTAL_DURATION)}px)` }}></div>
                  )}
                  <div className="absolute bottom-0 w-2 h-2 bg-red-500 z-30 pointer-events-none -translate-x-1/2" style={{ left: `calc(${trackLabelWidth * (1 - currentTime / TOTAL_DURATION)}px + ${(currentTime / TOTAL_DURATION) * 100}%)`, clipPath: 'polygon(0 0, 100% 0, 50% 100%)' }}></div>
              </div>

              {/* Tracks Container */}
              <div className="flex-1 flex flex-col overflow-hidden relative select-none">
                  {/* Track Visibility Toggle */}
                  <div className="absolute top-0 left-0 z-50">
                    <button 
                      onClick={() => setContextMenu(contextMenu?.type === 'visibility' ? null : {type: 'visibility' as any, targetId: 0, x: 4, y: 20})}
                      className="w-5 h-5 flex items-center justify-center text-slate-500 hover:text-white transition-colors rounded hover:bg-slate-700/50"
                      title="Gerenciar faixas"
                    >
                      <MoreVertical size={12} />
                    </button>
                    {contextMenu && (contextMenu as any).type === 'visibility' && (
                      <div className="absolute left-0 top-5 bg-slate-800 border border-slate-600 rounded-lg shadow-xl py-1 min-w-[140px] z-50"
                           onMouseLeave={() => setContextMenu(null)}>
                        <div className="px-2 py-1 text-[9px] font-bold text-slate-500 uppercase tracking-wider">Faixas</div>
                        {sortedTracks.map(track => {
                          const hasClips = clips.some(c => c.trackId === track.id && (c.parentId || null) === currentGroupId);
                          return (
                            <button key={track.id} 
                              onClick={() => setTracks(tracks.map(t => t.id === track.id ? {...t, hidden: !t.hidden} : t))}
                              className="w-full px-2 py-1 text-[10px] font-bold flex items-center gap-2 hover:bg-slate-700/50 transition-colors text-left"
                            >
                              <div className={`w-3 h-3 rounded-sm border flex items-center justify-center ${!track.hidden ? 'bg-purple-600 border-purple-500' : 'border-slate-500'}`}>
                                {!track.hidden && <Check size={8} className="text-white" />}
                              </div>
                              {track.type === 'visual' ? <Activity size={10} className="text-purple-400"/> : track.type === 'auditivo' ? <Volume2 size={10} className="text-emerald-400"/> : track.type === 'textual' ? <Type size={10} className="text-amber-400"/> : <FolderOpen size={10} className="text-blue-400"/>}
                              <span className={`${!track.hidden ? 'text-slate-200' : 'text-slate-500'}`}>{track.name}</span>
                              {!hasClips && <span className="text-slate-600 text-[8px] ml-auto">vazio</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Unified Scroll Container */}
                  <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar" ref={trackContainerRef}>
                    {sortedTracks
                      .filter(track => !track.hidden && clips.some(c => c.trackId === track.id && (c.parentId || null) === currentGroupId))
                      .map(track => (
                      <div key={track.id} className="flex h-8 shrink-0 border-b border-slate-700/30">
                        {/* Track Header */}
                        <div 
                          className="shrink-0 bg-slate-800/50 border-r border-slate-700 flex items-center justify-between px-1 group relative" 
                          style={{ width: trackLabelWidth }}
                          onContextMenu={(e) => { e.preventDefault(); setContextMenu({x: e.clientX, y: e.clientY, type: 'track', targetId: track.id}); }}
                        >
                           <div className="flex items-center gap-1 overflow-hidden" onDoubleClick={() => { const n = prompt('Nome', track.name); if(n) setTracks(tracks.map(t => t.id === track.id ? {...t, name: n} : t)); }}>
                              {track.type === 'visual' ? <Activity size={10} className="text-purple-400 shrink-0"/> : track.type === 'auditivo' ? <Volume2 size={10} className="text-emerald-400 shrink-0"/> : track.type === 'textual' ? <Type size={10} className="text-amber-400 shrink-0"/> : <FolderOpen size={10} className="text-blue-400 shrink-0"/>}
                              <span className="text-[9px] font-bold text-slate-300 truncate" title={track.name}>{track.name}</span>
                           </div>
                           <button className="text-slate-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity shrink-0" onClick={() => {
                               const newT = { id: Date.now(), name: track.name + ' (Nova)', type: track.type, color: 'transparent', order: track.order+1 };
                               setTracks([...tracks, newT]);
                           }}><Plus size={10}/></button>
                        </div>
                        {/* Track Body */}
                        <div className="flex-1 relative" style={{backgroundColor: track.color === 'transparent' ? 'transparent' : track.color}}
                             onDoubleClick={() => { setIsPlaying(false); }}>
                           {clips.filter(c => c.trackId === track.id && (c.parentId || null) === currentGroupId).map(clip => (
                              <div key={clip.id} 
                                   onContextMenu={(e) => { e.preventDefault(); setContextMenu({x: e.clientX, y: e.clientY, type: 'clip', targetId: clip.id}); }}
                                   onMouseDown={(e) => { 
    e.stopPropagation(); 
    if (e.ctrlKey) {
        handleGeminiVoiceTrigger(clip.id);
    } else {
        if (selectedClipId === clip.id && showProperties) {
            const tabs: ('info' | 'props' | 'anim' | 'actions' | 'projects')[] = ['info', 'props', 'anim', 'actions', 'projects'];
            const currentIdx = tabs.indexOf(activeTab);
            setActiveTab(tabs[(currentIdx + 1) % tabs.length]);
        } else {
            setSelectedClipId(clip.id); 
            setDraggingClip({ id: clip.id, startX: e.clientX, initialStartTime: clip.startTime, initialTrackId: clip.trackId }); 
        }
    }
}}
onDoubleClick={(e) => {
    e.stopPropagation();
    setCurrentTime(getAbsoluteTime(clip.id));
    setIsPlaying(false);
    if (clip.type === 'GROUP') {
        setCurrentGroupId(clip.id);
    } else {
        setSelectedClipId(clip.id);
        setShowProperties(true);
    }
}}
                                   className={`absolute top-1 bottom-1 rounded border text-[8px] px-1 py-0 font-bold truncate flex items-center gap-1 cursor-ew-resize transition-shadow ${selectedClipId === clip.id ? 'border-white text-white z-20 shadow-[0_0_10px_rgba(255,255,255,0.3)]' : 'border-slate-600 text-slate-300 hover:brightness-110 z-10'} ${track.type === 'visual' ? 'bg-purple-600' : track.type === 'auditivo' ? 'bg-emerald-600' : track.type === 'textual' ? 'bg-amber-600' : 'bg-blue-600'}`} 
                                   style={{ left: `${(getAbsoluteTime(clip.id) / TOTAL_DURATION) * 100}%`, width: `${(clip.duration / TOTAL_DURATION) * 100}%` }}>
                                {clip.type === 'textual' && <Type size={8} className="shrink-0"/>}
                                {clip.type === 'visual' && <ImageIcon size={8} className="shrink-0"/>}
                                <span className="truncate">{clip.text || clip.view || clip.id}</span>
                              </div>
                           ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Horizontal Resizer for Track Labels */}
                  <div 
                     className="absolute left-0 top-0 bottom-0 cursor-col-resize hover:bg-purple-500/50 active:bg-purple-500 z-40"
                     style={{ left: trackLabelWidth - 2, width: 4 }}
                     onMouseDown={(e) => {
                         e.preventDefault();
                         const startX = e.clientX;
                         const startWidth = trackLabelWidth;
                         const onMove = (me: MouseEvent) => setTrackLabelWidth(Math.max(60, Math.min(300, startWidth + (me.clientX - startX))));
                         const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                         window.addEventListener('mousemove', onMove);
                         window.addEventListener('mouseup', onUp);
                     }}
                  />
                  {/* Red Playhead Line */}
                  <div className="absolute top-0 bottom-0 w-[1px] bg-red-500 z-30 pointer-events-none" style={{ left: `calc(${trackLabelWidth * (1 - currentTime / TOTAL_DURATION)}px + ${(currentTime / TOTAL_DURATION) * 100}%)` }}></div>
              </div>
            </div>
          )}
        </div>
      )}
{/* GLOBAL TOAST */}
      <AnimatePresence>
        {showToast && (<motion.div initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} exit={{opacity:0, y:-20}} className="fixed top-12 left-1/2 -translate-x-1/2 bg-slate-800 text-emerald-400 px-8 py-4 rounded-full font-bold shadow-2xl flex items-center gap-3 text-lg z-[100] border border-slate-700"><CheckCircle2 size={24} />{showToast}</motion.div>)}
      </AnimatePresence>
    </PremiumBackground>
  );
};
